// pages/SAPUserMaintenance.js
// Dashboard admin-only untuk kelola user SAP (dialog) via SOAP/SOAMANAGER.
//   • List detail   → RFC_READ_TABLE (USR02 + USR21 → ADRP/ADR6) — dialog only
//   • Lock / Unlock  → BAPI_USER_LOCK / BAPI_USER_UNLOCK (single & multi-select)
//   • Reset password → BAPI_USER_CHANGE (password initial)
// Kredensial diambil server-side dari profil SAP Config (dipilih di dropdown).
import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { api } from '../context/AuthContext';
import { useLang } from '../context/LanguageContext';
import { usePageAIContext } from '../context/AIPageContext';
import {
  Users, Server, Lock, Unlock, KeyRound, RefreshCw, Search,
  AlertCircle, CheckCircle2, ShieldAlert, X, Mail, Building2, CalendarClock,
  BarChart3, Pencil, History,
} from 'lucide-react';
import SapLockTracePanel from '../components/SapLockTracePanel';
import useIsMobile from '../hooks/useIsMobile';
import CardList from '../components/mobile/CardList';
import DetailSheet from '../components/mobile/DetailSheet';

export default function SAPUserMaintenance() {
  const {
    lang,
    t
  } = useLang();
  const id = lang !== 'en';

  const [profiles, setProfiles] = useState([]);
  const [connId, setConnId]     = useState('');
  const [users, setUsers]       = useState(null);    // null = belum dimuat
  const [selected, setSelected] = useState(new Set());
  const [search, setSearch]     = useState('');
  const [activeOnly, setActiveOnly] = useState(true);
  const [lockedOnly, setLockedOnly] = useState(false);
  const [loading, setLoading]   = useState(false);
  const [bulkBusy, setBulkBusy] = useState(false);
  const [banner, setBanner]     = useState(null);
  const [busyUser, setBusyUser] = useState('');
  const [resetFor, setResetFor] = useState(null);
  const [editFor, setEditFor]   = useState(null); // objek user utk modal edit detail
  const [meta, setMeta]         = useState(null);
  const [snapshotAt, setSnapshotAt] = useState(null); // waktu snapshot DB (Load Users terakhir)
  const [fromCache, setFromCache]   = useState(false);
  const [sortKey, setSortKey]       = useState('username');
  const [sortDir, setSortDir]       = useState('asc');
  const [fCompany, setFCompany]     = useState('');
  const [fModule, setFModule]       = useState('');
  const [fLicense, setFLicense]     = useState('');
  const [showSummary, setShowSummary] = useState(false);
  const [licMap, setLicMap]           = useState({}); // kode → deskripsi (dari TUTYPA via master DB)
  const isMobile = useIsMobile();
  const [detailFor, setDetailFor] = useState(null); // username utk DetailSheet (mobile)
  const [lockTrace, setLockTrace] = useState(null); // null = tertutup; { user } = modal Lock Trace terbuka

  useEffect(() => {
    api.get('/api/sap/users/license-types').then(({ data }) => {
      const m = {}; (data.types || []).forEach(t => { m[t.lic_type] = t.description || ''; });
      setLicMap(m);
    }).catch(() => {});
  }, []);

  useEffect(() => {
    api.get('/api/sap/connections').then(({ data }) => setProfiles(data || [])).catch(() => setProfiles([]));
  }, []);

  // Default profil: begitu daftar profil datang, langsung pilih "ID / PRD — P01
  // (10.32.18.11)" tanpa perlu klik manual. Dicocokkan via sid (paling stabil);
  // host jadi fallback kalau suatu saat SID berubah. Hanya diterapkan sekali saat
  // connId masih kosong, jadi tak menimpa pilihan user yang sudah diganti manual.
  useEffect(() => {
    if (connId || !profiles.length) return;
    const p01 = profiles.find(p => String(p.sid || '').toUpperCase() === 'P01')
             || profiles.find(p => String(p.host || '').includes('10.32.18.11'));
    if (p01) setConnId(String(p01.id));
  }, [profiles, connId]);

  // Muat snapshot dari DB saat profil dipilih (instan, tanpa hit SAP).
  useEffect(() => {
    if (!connId) return;
    api.get(`/api/sap/users/cached?connectionId=${connId}`).then(({ data }) => {
      if (data.users?.length) { setUsers(data.users); setSnapshotAt(data.snapshotAt); setFromCache(true); }
    }).catch(() => {});
  }, [connId]);

  const profile = useMemo(() => profiles.find(p => String(p.id) === String(connId)) || null, [profiles, connId]);
  const canList = profile && profile.pathReadtable && profile.hasPassword;

  const missing = useMemo(() => {
    if (!profile) return [];
    const m = [];
    if (!profile.pathReadtable) m.push('RFC_READ_TABLE');
    if (!profile.pathLock)      m.push('LOCK');
    if (!profile.pathUnlock)    m.push('UNLOCK');
    if (!profile.pathChange)    m.push('CHANGE');
    return m;
  }, [profile]);

  const apiErr  = (e) => {
    const d = e?.response?.data;
    const base = d?.error || e?.message || (t('sapUser.errorOccurred'));
    return d?.detail ? `${base} — ${d.detail}` : base;
  };
  const apiMsgs = (e) => e?.response?.data?.messages || [];

  const loadUsers = useCallback(async () => {
    if (!connId) return;
    setLoading(true); setBanner(null); setUsers(null); setSelected(new Set()); setMeta(null);
    try {
      const { data } = await api.post('/api/sap/users/detail-list', { connectionId: Number(connId) });
      setUsers(data.users || []);
      setMeta(data.meta || null);
      setSnapshotAt(data.snapshotAt || null);
      setFromCache(false);
      setActiveOnly(true);
      if (!data.users?.length) setBanner({ type: 'info', text: t('sapUser.noDialogUsers') });
    } catch (e) {
      setBanner({ type: 'error', text: apiErr(e), msgs: apiMsgs(e) });
      setUsers([]);
    } finally { setLoading(false); }
  }, [connId, id]);

  const companies = useMemo(() => [...new Set((users || []).map(u => (u.company || '').trim()).filter(Boolean))].sort(), [users]);
  const modules   = useMemo(() => [...new Set((users || []).map(u => (u.module  || '').trim()).filter(Boolean))].sort(), [users]);
  // Label license: master TUTYPA (licMap) > licenseText snapshot > kode.
  const licLabel = (u) => licMap[u.licenseType] || (u.licenseText || '').trim() || (u.licenseType || '').trim();
  const licenses  = useMemo(() => [...new Set((users || []).map(u => (u.licenseType || '').trim()).filter(Boolean))].sort(), [users]);

  const filtered = useMemo(() => {
    let arr = users || [];
    if (activeOnly) arr = arr.filter(u => u.active);
    if (lockedOnly) arr = arr.filter(u => u.locked);
    if (fCompany) arr = arr.filter(u => (u.company || '').trim() === fCompany);
    if (fModule)  arr = arr.filter(u => (u.module  || '').trim() === fModule);
    if (fLicense) arr = arr.filter(u => (u.licenseType || '').trim() === fLicense);
    const q = search.trim().toLowerCase();
    if (q) arr = arr.filter(u =>
      u.username.toLowerCase().includes(q) ||
      (u.fullName || '').toLowerCase().includes(q) ||
      (u.email || '').toLowerCase().includes(q) ||
      (u.department || '').toLowerCase().includes(q) ||
      (u.company || '').toLowerCase().includes(q) ||
      (u.module || '').toLowerCase().includes(q));
    // Sort: klik header kolom (asc/desc). Boolean → angka agar konsisten.
    const dir = sortDir === 'asc' ? 1 : -1;
    return [...arr].sort((a, b) => {
      let va = a[sortKey], vb = b[sortKey];
      if (typeof va === 'boolean' || typeof vb === 'boolean') { va = va ? 1 : 0; vb = vb ? 1 : 0; return (va - vb) * dir; }
      return String(va ?? '').localeCompare(String(vb ?? ''), undefined, { numeric: true, sensitivity: 'base' }) * dir;
    });
  }, [users, activeOnly, lockedOnly, fCompany, fModule, fLicense, search, sortKey, sortDir]);

  const onSort = (key) => {
    if (sortKey === key) setSortDir(d => (d === 'asc' ? 'desc' : 'asc'));
    else { setSortKey(key); setSortDir('asc'); }
  };

  // ── AI page-context (Opsi B): ringkasan kondisi user untuk AI Assistant ─────
  // Set agregat menyeluruh (Opsi C) — semua dihitung di sini agar AKURAT & AI
  // tinggal membaca. Tetap agregat/daftar ter-clamp (bukan dump penuh) demi token.
  // company = 3 huruf pertama department (UPPERCASE).
  const aiSummary = useMemo(() => {
    if (!users || !users.length) return null;
    const compOf = (u) => {
      const c = (u.company || '').trim();
      if (c) return c.toUpperCase();
      const dept = (u.department || '').trim();
      return dept ? dept.slice(0, 3).toUpperCase() : '(tanpa dept)';
    };
    const total    = users.length;
    const active   = users.filter(u => u.active).length;
    const inactive = total - active;
    const locked   = users.filter(u => u.locked).length;
    const unlocked = total - locked;
    const noEmail  = users.filter(u => !(u.email || '').trim()).length;
    const noDept   = users.filter(u => !(u.department || '').trim()).length;

    // helper: hitung {kunci: jumlah} dari subset, urut desc, teks "A: n, B: m"
    const tally = (arr, keyFn, top = 500) => {
      const m = {};
      arr.forEach(u => { const k = keyFn(u); m[k] = (m[k] || 0) + 1; });
      const ent = Object.entries(m).sort((a, b) => b[1] - a[1]);
      const shown = ent.slice(0, top).map(([k, n]) => `${k}: ${n}`).join(', ');
      return ent.length > top ? `${shown}, …(+${ent.length - top} lagi)` : shown;
    };

    const lines = [];
    lines.push(`SAP User Maintenance — ringkasan user dialog (hanya user tipe Dialog).`);
    if (profile) lines.push(`Sistem: ${profile.sid || '?'} (${profile.landscape || ''}/${profile.environment || ''}, host ${profile.host || '?'}, client ${profile.client || '?'}).`);
    lines.push(`Definisi: "company" = 3 huruf pertama department (uppercase). "aktif"=account tidak expired/valid; "terkunci"=locked (mis. karena admin atau salah password).`);

    // Total global
    lines.push(`TOTAL: ${total} user. Aktif: ${active}. Nonaktif: ${inactive}. Terkunci: ${locked}. Tidak terkunci: ${unlocked}. Tanpa email: ${noEmail}. Tanpa department: ${noDept}.`);

    // Per company: total / aktif / terkunci
    lines.push(`USER PER COMPANY (total): ${tally(users, compOf)}`);
    lines.push(`USER AKTIF PER COMPANY: ${tally(users.filter(u => u.active), compOf)}`);
    lines.push(`USER TERKUNCI PER COMPANY: ${tally(users.filter(u => u.locked), compOf) || '(tidak ada)'}`);

    // Per department (kode masih perlu jawaban per-dept juga)
    lines.push(`USER PER DEPARTMENT (total): ${tally(users, u => (u.department || '').trim() || '(tanpa dept)')}`);

    // Daftar user terkunci (maks 40): username, nama, company, alasan
    if (locked) {
      const lockedList = users.filter(u => u.locked).slice(0, 300)
        .map(u => `${u.username}${u.fullName ? ` (${u.fullName})` : ''} [${compOf(u)}]${u.lockReason ? ` alasan:${u.lockReason}` : ''}`);
      lines.push(`DAFTAR USER TERKUNCI: ${lockedList.join('; ')}${locked > 300 ? ` …(+${locked - 300} lagi)` : ''}`);
    }

    // Daftar user nonaktif (maks 40)
    if (inactive) {
      const inactiveList = users.filter(u => !u.active).slice(0, 300)
        .map(u => `${u.username}${u.fullName ? ` (${u.fullName})` : ''} [${compOf(u)}]`);
      lines.push(`DAFTAR USER NONAKTIF: ${inactiveList.join('; ')}${inactive > 300 ? ` …(+${inactive - 300} lagi)` : ''}`);
    }

    // Validity/expiry — hanya bila field terisi; tampilkan apa adanya (tanpa parsing tanggal).
    const withValidity = users.filter(u => (u.validThrough || '').toString().trim());
    if (withValidity.length) {
      const vList = withValidity.slice(0, 300).map(u => `${u.username}: ${u.validThrough}`);
      lines.push(`MASA BERLAKU (validThrough) — ${withValidity.length} user punya nilai: ${vList.join('; ')}${withValidity.length > 300 ? ` …(+${withValidity.length - 300} lagi)` : ''}`);
    }

    return lines.join('\n');
  }, [users, profile]);

  usePageAIContext('sap_user_mgmt', 'SAP User Maintenance', aiSummary);

  const allSelected = filtered.length > 0 && filtered.every(u => selected.has(u.username));
  const toggleAll = () => setSelected(prev => {
    const n = new Set(prev);
    if (allSelected) filtered.forEach(u => n.delete(u.username));
    else filtered.forEach(u => n.add(u.username));
    return n;
  });
  const toggleOne = (u) => setSelected(prev => { const n = new Set(prev); n.has(u) ? n.delete(u) : n.add(u); return n; });

  const setLockedLocal = (names, val) => setUsers(prev => (prev || []).map(u =>
    // 'Locked by admin' — istilah PERSIS seperti decodeUflag(64) di backend.
    // Aksi Lock dari halaman ini memang menyetel UFLAG bit admin, jadi tebakan
    // optimistis ini akan sama dengan hasil refresh berikutnya dari SAP.
    names.includes(u.username) ? { ...u, locked: val, lockReason: val ? (u.lockReason || 'Locked by admin') : '' } : u));

  const singleAction = async (endpoint, username, lock, okText) => {
    setBusyUser(username); setBanner(null);
    try {
      const { data } = await api.post(endpoint, { connectionId: Number(connId), username });
      const detail = (data.messages || []).map(m => m.message).filter(Boolean).join(' · ');
      setLockedLocal([username], lock);
      setBanner({ type: 'success', text: `${okText}: ${username}${detail ? ` — ${detail}` : ''}` });
    } catch (e) {
      setBanner({ type: 'error', text: `${apiErr(e)} (${username})`, msgs: apiMsgs(e) });
    } finally { setBusyUser(''); }
  };
  const doLock   = (u) => window.confirm((id ? 'Lock user ' : 'Lock user ') + u + '?')   && singleAction('/api/sap/users/lock',   u, true,  t('sapUser.locked'));
  const doUnlock = (u) => window.confirm((id ? 'Unlock user ' : 'Unlock user ') + u + '?') && singleAction('/api/sap/users/unlock', u, false, t('sapUser.unlocked'));

  const bulkLockUnlock = async (lock) => {
    const names = [...selected];
    if (!names.length) return;
    const verb = lock ? 'Lock' : 'Unlock';
    if (!window.confirm(`${verb} ${names.length} user?`)) return;
    setBulkBusy(true); setBanner(null);
    const endpoint = lock ? '/api/sap/users/lock' : '/api/sap/users/unlock';
    let ok = 0; const fails = [];
    for (const u of names) {
      try { await api.post(endpoint, { connectionId: Number(connId), username: u }); ok++; }
      catch { fails.push(u); }
    }
    setLockedLocal(names.filter(n => !fails.includes(n)), lock);
    setSelected(new Set());
    setBulkBusy(false);
    setBanner({
      type: fails.length ? 'error' : 'success',
      text: `${verb}: ${ok} ${t('sapUser.ok')}${fails.length ? `, ${fails.length} ${t('sapUser.failed')} (${fails.join(', ')})` : ''}.`,
    });
  };

  return (
    <div className="p-6 max-w-6xl mx-auto">
      {/* Peringatan keamanan */}
      <div className="mb-5 flex items-start gap-3 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3">
        <ShieldAlert className="text-amber-500 flex-shrink-0 mt-0.5" style={{ width: 18, height: 18 }} />
        <p className="text-xs text-amber-700 leading-relaxed">
          {t('sapUser.actionsHereWriteDirectlySap')}
        </p>
      </div>
      {/* Pemilih profil */}
      <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm mb-5">
        <div className="flex items-center gap-2 mb-3">
          <Server className="text-blue-500" style={{ width: 16, height: 16 }} />
          <h3 className="font-display font-bold text-slate-800 text-sm">{t('sapUser.selectSapSystem')}</h3>
        </div>
        <div className="flex flex-wrap items-end gap-3">
          <div className="flex-1 min-w-[220px]">
            <label className="block text-xs font-semibold text-slate-500 mb-1">{t('sapUser.profileSapConfig')}</label>
            <select
              value={connId}
              onChange={(e) => { setConnId(e.target.value); setUsers(null); setBanner(null); setSelected(new Set()); setSnapshotAt(null); setFromCache(false); setFCompany(''); setFModule(''); setFLicense(''); setActiveOnly(true); }}
              className="w-full px-3 py-2 rounded-xl border border-slate-200 text-sm bg-white outline-none focus:border-blue-300"
            >
              <option value="">{t('sapUser.selectProfile')}</option>
              {profiles.map(p => (
                <option key={p.id} value={p.id}>
                  {p.landscape}{p.environment ? ` / ${p.environment}` : ''}{p.sid ? ` — ${p.sid}` : ''}{p.client ? `/${p.client}` : ''}{p.host ? ` (${p.host})` : ''}
                </option>
              ))}
            </select>
          </div>
          <button
            onClick={loadUsers} disabled={!canList || loading}
            className={`flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-semibold border transition-all
              ${(!canList || loading) ? 'bg-slate-100 text-slate-400 border-slate-200 cursor-not-allowed' : 'bg-blue-600 text-white border-blue-600 hover:bg-blue-700'}`}
          >
            <RefreshCw style={{ width: 15, height: 15 }} className={loading ? 'animate-spin' : ''} />
            {loading ? (t('sapUser.loading')) : (t('sapUser.loadUsers'))}
          </button>
          <button
            onClick={() => setLockTrace({ user: '' })} disabled={!connId}
            title="Failed logons & user locks from SM20"
            className="flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-semibold border border-rose-200 bg-rose-50 text-rose-700 hover:bg-rose-100 disabled:opacity-40 disabled:cursor-not-allowed"
          >
            <History style={{ width: 15, height: 15 }} /> Lock Trace
          </button>
        </div>
        {profile && (
          <div className="mt-3 text-xs">
            {!profile.hasPassword && <p className="text-rose-600 mb-1">{t('sapUser.profileHasNoPasswordSet')}</p>}
            {missing.length > 0 && (
              <p className="text-amber-600">
                {t('sapUser.missingServicePaths')}
                <span className="font-semibold">{missing.join(', ')}</span>
                {t('sapUser.relatedListActionsBlockedUntil')}
              </p>
            )}
          </div>
        )}
      </div>
      {/* Banner hasil */}
      {banner && (
        <div className={`mb-4 flex items-start gap-3 rounded-xl border px-4 py-3 text-sm
          ${banner.type === 'success' ? 'border-emerald-200 bg-emerald-50 text-emerald-700'
            : banner.type === 'error' ? 'border-rose-200 bg-rose-50 text-rose-700' : 'border-blue-200 bg-blue-50 text-blue-700'}`}>
          {banner.type === 'success' ? <CheckCircle2 style={{ width: 18, height: 18 }} className="flex-shrink-0 mt-0.5" /> : <AlertCircle style={{ width: 18, height: 18 }} className="flex-shrink-0 mt-0.5" />}
          <div className="flex-1 min-w-0">
            <p>{banner.text}</p>
            {(banner.msgs || []).length > 0 && (
              <ul className="mt-1 list-disc pl-4 text-xs opacity-80">{banner.msgs.map((m, i) => <li key={i}>[{m.type}] {m.message}</li>)}</ul>
            )}
          </div>
          <button onClick={() => setBanner(null)} className="flex-shrink-0 opacity-60 hover:opacity-100"><X style={{ width: 16, height: 16 }} /></button>
        </div>
      )}
      {/* Daftar user */}
      {users !== null && (
        <div className="rounded-2xl border border-slate-200 bg-white shadow-sm overflow-hidden">
          {/* Toolbar: filter + search + bulk */}
          <div className="flex flex-wrap items-center justify-between gap-3 px-5 py-3 border-b border-slate-100">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="font-display font-bold text-slate-800 text-sm flex items-center gap-2">
                <Users className="text-blue-500" style={{ width: 16, height: 16 }} />
                {t('sapUser.dialogUsers')} <span className="text-slate-400 font-medium">({filtered.length}/{users.length})</span>
              </span>
              <FilterPill active={activeOnly} onClick={() => setActiveOnly(v => !v)} label={t('sapUser.activeOnly')} />
              <FilterPill active={lockedOnly} onClick={() => setLockedOnly(v => !v)} label={t('sapUser.lockedOnly')} />
              <select value={fCompany} onChange={(e) => setFCompany(e.target.value)}
                className="px-2 py-1 rounded-lg border border-slate-200 text-xs bg-white outline-none focus:border-blue-300">
                <option value="">{t('sapUser.companyAll')}</option>
                {companies.map(c => <option key={c} value={c}>{c}</option>)}
              </select>
              <select value={fModule} onChange={(e) => setFModule(e.target.value)}
                className="px-2 py-1 rounded-lg border border-slate-200 text-xs bg-white outline-none focus:border-blue-300">
                <option value="">{t('sapUser.moduleAll')}</option>
                {modules.map(m => <option key={m} value={m}>{m}</option>)}
              </select>
              <select value={fLicense} onChange={(e) => setFLicense(e.target.value)}
                className="px-2 py-1 rounded-lg border border-slate-200 text-xs bg-white outline-none focus:border-blue-300">
                <option value="">{t('sapUser.licenseAll')}</option>
                {licenses.map(l => <option key={l} value={l}>{l}</option>)}
              </select>
              <button onClick={() => setShowSummary(true)} disabled={!users?.length}
                className="flex items-center gap-1 px-2.5 py-1 rounded-lg text-xs font-semibold border border-violet-200 bg-violet-50 text-violet-700 hover:bg-violet-100 disabled:opacity-40">
                <BarChart3 style={{ width: 13, height: 13 }} /> {t('sapUser.summary')}
              </button>
              {snapshotAt && (
                <span className="text-[11px] text-slate-400" title={fromCache ? (t('sapUser.dbSnapshotClickLoadUsers')) : ''}>
                  {t('sapUser.dataAs')} {new Date(snapshotAt).toLocaleString()}{fromCache ? (id ? ' (snapshot)' : ' (snapshot)') : ''}
                </span>
              )}
            </div>
            <div className="relative">
              <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-300" style={{ width: 14, height: 14 }} />
              <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder={t('sapUser.searchUserNameEmail')}
                className="pl-8 pr-3 py-1.5 rounded-lg border border-slate-200 text-sm outline-none focus:border-blue-300 w-56" />
            </div>
          </div>

          {/* Diagnostik enrichment (nama/dept/email) */}
          {meta && Object.keys(meta.errors || {}).length > 0 && (
            <div className="px-5 py-2 bg-amber-50 border-b border-amber-100 text-xs text-amber-700">
              {t('sapUser.someEnrichmentFailed')}
              {Object.entries(meta.errors).map(([k, v]) => `${k.toUpperCase()}: ${v}`).join(' · ')}
              {' '}<span className="text-amber-500">(USR21={meta.usr21}, ADRP={meta.adrp}, ADCP={meta.adcp}, ADR6={meta.adr6})</span>
            </div>
          )}

          {/* Bulk action bar */}
          {selected.size > 0 && (
            <div className="flex items-center gap-3 px-5 py-2.5 bg-blue-50 border-b border-blue-100">
              <span className="text-sm font-semibold text-blue-700">{selected.size} {t('sapUser.selected')}</span>
              <button onClick={() => bulkLockUnlock(true)} disabled={bulkBusy || !profile?.pathLock}
                className="flex items-center gap-1 px-3 py-1.5 rounded-lg text-xs font-semibold border border-amber-300 bg-white text-amber-700 hover:bg-amber-50 disabled:opacity-40">
                <Lock style={{ width: 13, height: 13 }} /> {t('sapUser.lockSelected')}
              </button>
              <button onClick={() => bulkLockUnlock(false)} disabled={bulkBusy || !profile?.pathUnlock}
                className="flex items-center gap-1 px-3 py-1.5 rounded-lg text-xs font-semibold border border-emerald-300 bg-white text-emerald-700 hover:bg-emerald-50 disabled:opacity-40">
                <Unlock style={{ width: 13, height: 13 }} /> {t('sapUser.unlockSelected')}
              </button>
              {bulkBusy && <RefreshCw className="animate-spin text-blue-500" style={{ width: 14, height: 14 }} />}
              <button onClick={() => setSelected(new Set())} className="ml-auto text-xs text-slate-500 hover:text-slate-700">{t('sapUser.clear')}</button>
            </div>
          )}

          {filtered.length === 0 ? (
            <div className="px-5 py-10 text-center text-slate-400 text-sm">{t('sapUser.noUsersMatchFilter')}</div>
          ) : isMobile ? (
            <CardList
              items={filtered}
              keyOf={(u) => u.username}
              selected={selected}
              onToggle={toggleOne}
              title={(u) => u.username}
              badge={(u) => u.locked
                ? { label: 'Locked', tone: 'rose', Icon: Lock }
                : { label: t('sapUser.active'), tone: 'emerald', Icon: Unlock }}
              subtitle={(u) => [u.fullName, u.department, u.email].filter(Boolean).join(' · ') || '—'}
              onOpen={(u) => setDetailFor(u.username)}
              emptyText={t('sapUser.noUsersMatchFilter')}
            />
          ) : (
            <div className="max-h-[62vh] overflow-y-auto">
              <table className="w-full text-sm">
                <thead className="sticky top-0 bg-slate-50 z-10">
                  <tr className="text-xs uppercase tracking-wide text-slate-500">
                    <th className="px-4 py-2.5 w-10"><input type="checkbox" checked={allSelected} onChange={toggleAll} className="rounded border-slate-300" /></th>
                    <SortTh k="username"     label="Username"                         sortKey={sortKey} sortDir={sortDir} onSort={onSort} />
                    <SortTh k="fullName"     label={t('sapUser.fullName')} sortKey={sortKey} sortDir={sortDir} onSort={onSort} />
                    <SortTh k="department"   label="Dept"                             sortKey={sortKey} sortDir={sortDir} onSort={onSort} />
                    <SortTh k="company"      label="Company"                          sortKey={sortKey} sortDir={sortDir} onSort={onSort} />
                    <SortTh k="module"       label="Module"                           sortKey={sortKey} sortDir={sortDir} onSort={onSort} />
                    <SortTh k="licenseType"  label="License"                          sortKey={sortKey} sortDir={sortDir} onSort={onSort} />
                    <SortTh k="email"        label="Email"                            sortKey={sortKey} sortDir={sortDir} onSort={onSort} />
                    <SortTh k="validThrough" label={t('sapUser.validThrough')} sortKey={sortKey} sortDir={sortDir} onSort={onSort} />
                    <SortTh k="locked"       label="Status"                           sortKey={sortKey} sortDir={sortDir} onSort={onSort} />
                    {/* Alasan terkunci. Istilahnya datang dari decodeUflag() di
                        backend — sama dengan tabel "Locked users" di SAP Basis
                        Monitor. Memakai kembali key i18n 'basis.reason' supaya
                        judul kolomnya identik di kedua halaman. */}
                    <SortTh k="lockReason"   label={t('basis.reason')}                 sortKey={sortKey} sortDir={sortDir} onSort={onSort} />
                    <th className="text-right px-4 py-2.5 font-semibold sticky right-0 bg-slate-50 shadow-[-6px_0_8px_-6px_rgba(15,23,42,0.15)]">{t('sapUser.actions')}</th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.map((u, i) => {
                    const busy = busyUser === u.username;
                    const sel = selected.has(u.username);
                    return (
                      <tr key={u.username} className={`${sel ? 'bg-blue-50/50' : i % 2 ? 'bg-white' : 'bg-slate-50/40'} border-b border-slate-50`}>
                        <td className="px-4 py-2"><input type="checkbox" checked={sel} onChange={() => toggleOne(u.username)} className="rounded border-slate-300" /></td>
                        <td className="px-3 py-2 font-semibold text-slate-700">{u.username}</td>
                        <td className="px-3 py-2 text-slate-600">{u.fullName || <span className="text-slate-300">—</span>}</td>
                        <td className="px-3 py-2 text-slate-500">{u.department || <span className="text-slate-300">—</span>}</td>
                        <td className="px-3 py-2 text-slate-600 font-medium">{u.company || <span className="text-slate-300">—</span>}</td>
                        <td className="px-3 py-2 text-slate-500">{u.module || <span className="text-slate-300">—</span>}</td>
                        <td className="px-3 py-2 text-slate-500" title={u.licenseType}>{u.licenseType ? (licLabel(u) || u.licenseType) : <span className="text-slate-300">—</span>}</td>
                        <td className="px-3 py-2 text-slate-500 truncate max-w-[180px]" title={u.email}>{u.email || <span className="text-slate-300">—</span>}</td>
                        <td className="px-3 py-2 text-slate-500">{u.validThrough || <span className="text-emerald-500" title={t('sapUser.noExpiry')}>∞</span>}</td>
                        <td className="px-3 py-2">
                          {u.locked
                            ? <span title={u.lockReason} className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-rose-50 text-rose-600 border border-rose-200"><Lock style={{ width: 11, height: 11 }} /> Locked</span>
                            : <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-emerald-50 text-emerald-600 border border-emerald-200"><Unlock style={{ width: 11, height: 11 }} /> {t('sapUser.active')}</span>}
                        </td>
                        <td className="px-3 py-2 text-slate-500 whitespace-nowrap" title={u.lockReason}>
                          {u.lockReason || <span className="text-slate-300">—</span>}
                        </td>
                        <td className={`px-4 py-2 sticky right-0 shadow-[-6px_0_8px_-6px_rgba(15,23,42,0.15)] ${sel ? 'bg-blue-50' : i % 2 ? 'bg-white' : 'bg-slate-50'}`}>
                          <div className="flex items-center justify-end gap-1.5">
                            <button onClick={() => doLock(u.username)} disabled={busy || u.locked || !profile?.pathLock} title="Lock"
                              className="flex items-center gap-1 px-2 py-1 rounded-lg text-xs font-medium border border-amber-200 bg-amber-50 text-amber-700 hover:bg-amber-100 disabled:opacity-30 disabled:cursor-not-allowed"><Lock style={{ width: 13, height: 13 }} /></button>
                            <button onClick={() => doUnlock(u.username)} disabled={busy || !u.locked || !profile?.pathUnlock} title="Unlock"
                              className="flex items-center gap-1 px-2 py-1 rounded-lg text-xs font-medium border border-emerald-200 bg-emerald-50 text-emerald-700 hover:bg-emerald-100 disabled:opacity-30 disabled:cursor-not-allowed"><Unlock style={{ width: 13, height: 13 }} /></button>
                            <button onClick={() => setEditFor(u)} disabled={busy || !profile?.pathChange} title={t('sapUser.editDetails')}
                              className="flex items-center gap-1 px-2 py-1 rounded-lg text-xs font-medium border border-slate-200 bg-slate-50 text-slate-600 hover:bg-slate-100 disabled:opacity-30 disabled:cursor-not-allowed"><Pencil style={{ width: 13, height: 13 }} /></button>
                            <button onClick={() => setResetFor(u.username)} disabled={busy || !profile?.pathChange} title={id ? 'Reset password' : 'Reset password'}
                              className="flex items-center gap-1 px-2 py-1 rounded-lg text-xs font-medium border border-blue-200 bg-blue-50 text-blue-700 hover:bg-blue-100 disabled:opacity-30 disabled:cursor-not-allowed"><KeyRound style={{ width: 13, height: 13 }} /></button>
                            <button onClick={() => setLockTrace({ user: u.username })} title="Lock Trace (SM20)"
                              className={`flex items-center gap-1 px-2 py-1 rounded-lg text-xs font-medium border ${u.locked ? 'border-rose-300 bg-rose-100 text-rose-700 hover:bg-rose-200' : 'border-slate-200 bg-slate-50 text-slate-500 hover:bg-slate-100'}`}><History style={{ width: 13, height: 13 }} /></button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
      {/* Mobile: bottom-sheet detail — semua kolom + aksi (item live dari state) */}
      {isMobile && detailFor && (() => {
        const u = (users || []).find((x) => x.username === detailFor);
        if (!u) return null;
        const busy = busyUser === u.username;
        return (
          <DetailSheet
            open
            title={u.username}
            badge={u.locked
              ? <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-medium bg-rose-50 text-rose-600 border border-rose-200"><Lock style={{ width: 11, height: 11 }} /> Locked</span>
              : <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-medium bg-emerald-50 text-emerald-600 border border-emerald-200"><Unlock style={{ width: 11, height: 11 }} /> {t('sapUser.active')}</span>}
            fields={[
              { label: t('sapUser.fullName'), value: u.fullName },
              { label: 'Dept', value: u.department },
              { label: 'Company', value: u.company },
              { label: 'Module', value: u.module },
              { label: 'License', value: u.licenseType ? `${u.licenseType}${licLabel(u) && licLabel(u) !== u.licenseType ? ` — ${licLabel(u)}` : ''}` : '' },
              { label: 'Email', value: u.email },
              { label: t('sapUser.validThrough'), value: u.validThrough || (t('sapUser.noExpiry2')) },
              ...(u.locked && u.lockReason ? [{ label: t('sapUser.lockReason'), value: u.lockReason }] : []),
            ]}
            actions={[
              { key: 'lock',   label: 'Lock',   Icon: Lock,     tone: 'amber',   busy,
                disabled: u.locked || !profile?.pathLock,   onClick: () => doLock(u.username) },
              { key: 'unlock', label: 'Unlock', Icon: Unlock,   tone: 'emerald', busy,
                disabled: !u.locked || !profile?.pathUnlock, onClick: () => doUnlock(u.username) },
              { key: 'edit',   label: t('sapUser.editDetails2'), Icon: Pencil, tone: 'slate', busy,
                disabled: !profile?.pathChange, onClick: () => setEditFor(u) },
              { key: 'reset',  label: id ? 'Reset Password' : 'Reset Password', Icon: KeyRound, tone: 'blue', busy,
                disabled: !profile?.pathChange, onClick: () => setResetFor(u.username) },
              { key: 'trace',  label: 'Lock Trace', Icon: History, tone: 'rose',
                onClick: () => { setDetailFor(null); setLockTrace({ user: u.username }); } },
            ]}
            onClose={() => setDetailFor(null)}
          />
        );
      })()}
      {lockTrace && connId && (
        <SapLockTracePanel
          connId={connId}
          initialUser={lockTrace.user}
          profileLabel={profile ? `${profile.sid || profile.host}/${profile.client}` : ''}
          onClose={() => setLockTrace(null)}
        />
      )}
      {showSummary && users?.length > 0 && (
        <SummaryModal users={users} id={id} licMap={licMap} onClose={() => setShowSummary(false)} />
      )}
      {editFor && (
        <EditUserModal user={editFor} id={id} busy={busyUser === editFor.username}
          onClose={() => setEditFor(null)}
          onSubmit={async (changes) => {
            const uname = editFor.username;
            setBusyUser(uname); setBanner(null);
            try {
              await api.post('/api/sap/users/update-detail', { connectionId: Number(connId), username: uname, changes });
              // Update state lokal agar tabel langsung mencerminkan perubahan.
              setUsers(prev => (prev || []).map(u => {
                if (u.username !== uname) return u;
                const nu = { ...u };
                if ('firstName' in changes || 'lastName' in changes) {
                  const parts = (u.fullName || '').trim().split(/\s+/);
                  const oldFirst = parts.slice(0, -1).join(' ') || parts[0] || '';
                  const oldLast  = parts.length > 1 ? parts[parts.length - 1] : '';
                  nu.fullName = `${'firstName' in changes ? changes.firstName : oldFirst} ${'lastName' in changes ? changes.lastName : oldLast}`.trim();
                }
                if ('department' in changes) {
                  nu.department = changes.department;
                  const lsKL = (profile?.landscape || '').trim().toUpperCase() === 'KL';
                  const di = nu.department.indexOf('-');
                  nu.company = lsKL ? 'PIL' : (di > 0 ? nu.department.substring(0, di) : nu.department);
                  nu.module  = di > 0 ? nu.department.substring(di + 1) : nu.department;
                }
                if ('email' in changes)        nu.email = changes.email;
                if ('validFrom' in changes)    nu.validFrom = changes.validFrom;
                if ('validThrough' in changes) nu.validThrough = changes.validThrough;
                return nu;
              }));
              setBanner({ type: 'success', text: `${t('sapUser.userDetailsUpdated')}: ${uname}` });
              setEditFor(null);
            } catch (e) {
              setBanner({ type: 'error', text: `${apiErr(e)} (${uname})`, msgs: apiMsgs(e) });
            } finally { setBusyUser(null); }
          }} />
      )}
      {resetFor && (
        <ResetPasswordModal username={resetFor} id={id} busy={busyUser === resetFor}
          onClose={() => setResetFor(null)}
          onSubmit={async (pw) => {
            setBusyUser(resetFor); setBanner(null);
            try {
              const { data } = await api.post('/api/sap/users/reset-password', { connectionId: Number(connId), username: resetFor, newPassword: pw });
              const detail = (data.messages || []).map(m => m.message).filter(Boolean).join(' · ');
              setBanner({ type: 'success', text: `${t('sapUser.passwordReset')}: ${resetFor}${detail ? ` — ${detail}` : ''}` });
              setResetFor(null);
            } catch (e) {
              setBanner({ type: 'error', text: `${apiErr(e)} (${resetFor})`, msgs: apiMsgs(e) });
            } finally { setBusyUser(''); }
          }} />
      )}
    </div>
  );
}

function EditField({ label, value, onChange, type = 'text', placeholder }) {
  return (
    <div>
      <label className="block text-[11px] font-semibold text-slate-500 mb-1">{label}</label>
      <input type={type} value={value} onChange={onChange} placeholder={placeholder}
        className="w-full px-3 py-2 rounded-lg border border-slate-200 text-sm outline-none focus:border-blue-300" />
    </div>
  );
}

// Modal edit detail user: first/last name, department, email, valid from/through.
// Hanya field yang diubah yang dikirim ke BAPI_USER_CHANGE.
function EditUserModal({ user, id, busy, onClose, onSubmit }) {
  const {
    t
  } = useLang();

  // Prefill first/last dari fullName (kata terakhir = last name) — bisa dikoreksi manual.
  const parts = (user.fullName || '').trim().split(/\s+/).filter(Boolean);
  const init = {
    firstName:    parts.length > 1 ? parts.slice(0, -1).join(' ') : (parts[0] || ''),
    lastName:     parts.length > 1 ? parts[parts.length - 1] : '',
    department:   user.department || '',
    email:        user.email || '',
    validFrom:    /^\d{4}-\d{2}-\d{2}$/.test(user.validFrom || '') ? user.validFrom : '',
    validThrough: /^\d{4}-\d{2}-\d{2}$/.test(user.validThrough || '') ? user.validThrough : '',
  };
  const [form, setForm] = useState(init);
  const set = (k) => (e) => setForm(f => ({ ...f, [k]: e.target.value }));
  const changes = Object.fromEntries(Object.entries(form).filter(([k, v]) => v !== init[k]));
  const dirty = Object.keys(changes).length > 0;

  return (
    <div className="fixed inset-0 z-[1000] flex items-center justify-center bg-slate-900/50 p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-md p-5" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-display font-bold text-slate-800 text-sm flex items-center gap-2">
            <Pencil className="text-slate-500" style={{ width: 15, height: 15 }} /> {t('sapUser.editUserDetails')} — {user.username}
          </h3>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-600"><X style={{ width: 18, height: 18 }} /></button>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <EditField label={t('sapUser.firstName')} value={form.firstName} onChange={set('firstName')} />
          <EditField label={t('sapUser.lastName')} value={form.lastName} onChange={set('lastName')} />
          <div className="col-span-2"><EditField label="Department" value={form.department} onChange={set('department')} placeholder="mis. PMI-FICO" /></div>
          <div className="col-span-2"><EditField label="Email" value={form.email} onChange={set('email')} type="email" /></div>
          <EditField label={t('sapUser.valid')} value={form.validFrom} onChange={set('validFrom')} type="date" />
          <EditField label={t('sapUser.validThrough')} value={form.validThrough} onChange={set('validThrough')} type="date" />
        </div>
        <p className="text-[11px] text-slate-400 mt-2">
          {t('sapUser.emptyDateUnlimitedOnlyChanged')}
        </p>
        <div className="flex justify-end gap-2 mt-4">
          <button onClick={onClose} className="px-3 py-1.5 rounded-lg text-xs font-semibold border border-slate-200 text-slate-600 hover:bg-slate-50">{t('sapUser.cancel')}</button>
          <button onClick={() => onSubmit(changes)} disabled={!dirty || busy}
            className="px-3 py-1.5 rounded-lg text-xs font-semibold bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-40">
            {busy ? (t('sapUser.saving')) : (t('sapUser.saveSap'))}
          </button>
        </div>
      </div>
    </div>
  );
}

// Ringkasan/insight — hanya user AKTIF. Kiri: matriks license per company-module
// (format License Summary USMM). Kanan: donut per company. Bawah: pie distribusi
// license keseluruhan + legend.
const SUM_COLORS = ['#10b981', '#3b82f6', '#f59e0b', '#8b5cf6', '#ef4444', '#14b8a6', '#f43f5e', '#0ea5e9', '#a3a3a3'];

function DonutChart({ slices, size = 96, stroke = 14, title, subtitle }) {
  const total = slices.reduce((a, s) => a + s.value, 0) || 1;
  const r = (size - stroke) / 2, C = 2 * Math.PI * r;
  let acc = 0;
  return (
    <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
      <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke="#f1f5f9" strokeWidth={stroke} />
      {slices.map((sl, i) => {
        const frac = sl.value / total, off = acc; acc += frac;
        return sl.value ? (
          <circle key={i} cx={size / 2} cy={size / 2} r={r} fill="none"
            stroke={sl.color} strokeWidth={stroke}
            strokeDasharray={`${frac * C} ${C}`} strokeDashoffset={-off * C}
            transform={`rotate(-90 ${size / 2} ${size / 2})`}>
            <title>{`${sl.label}: ${sl.value}`}</title>
          </circle>
        ) : null;
      })}
      <text x="50%" y="47%" textAnchor="middle" fontSize={size * 0.14} fontWeight="700" fill="#334155">{title}</text>
      <text x="50%" y="62%" textAnchor="middle" fontSize={size * 0.11} fill="#94a3b8">{subtitle}</text>
    </svg>
  );
}

function SummaryModal({ users, id, licMap, onClose }) {
  const {
    t
  } = useLang();

  const data = useMemo(() => {
    // User aktif, kecualikan license code 91 (mis. Test/technical — tidak dihitung).
    const act = users.filter(u => u.active && (u.licenseType || '').trim().toUpperCase() !== '91');
    const licName = (u) => (licMap[u.licenseType] || '').trim() || (u.licenseText || '').trim() || (u.licenseType || '').trim() || (t('sapUser.noLicense'));
    const licSet = new Set();
    const byComp = new Map();
    for (const u of act) {
      const c = (u.company || '').trim() || (t('sapUser.blank'));
      const m = (u.module  || '').trim() || '—';
      const l = licName(u); licSet.add(l);
      if (!byComp.has(c)) byComp.set(c, new Map());
      const mods = byComp.get(c);
      if (!mods.has(m)) mods.set(m, new Map());
      const lm = mods.get(m);
      lm.set(l, (lm.get(l) || 0) + 1);
    }
    const lics = [...licSet].sort();
    const colorOf = Object.fromEntries(lics.map((l, i) => [l, SUM_COLORS[i % SUM_COLORS.length]]));
    const companies = [...byComp.entries()]
      .map(([c, mods]) => {
        const rows = [...mods.entries()]
          .map(([m, lm]) => ({ module: m, counts: lics.map(l => lm.get(l) || 0) }))
          .sort((a, b) => a.module.localeCompare(b.module));
        const sub = lics.map((_, i) => rows.reduce((t, r) => t + r.counts[i], 0));
        return { company: c, rows, sub, total: sub.reduce((a, b) => a + b, 0) };
      })
      .sort((a, b) => b.total - a.total);
    const grand = lics.map((_, i) => companies.reduce((t, c) => t + c.sub[i], 0));
    return { lics, colorOf, companies, grand, grandTotal: grand.reduce((a, b) => a + b, 0), activeCount: act.length };
  }, [users, id, licMap]);

  return (
    <div className="fixed inset-0 z-[1000] flex items-center justify-center bg-slate-900/50 p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-5xl p-5 max-h-[88vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-1">
          <h3 className="font-display font-bold text-slate-800 text-sm flex items-center gap-2">
            <BarChart3 className="text-violet-500" style={{ width: 16, height: 16 }} />
            {t('sapUser.licenseSummaryActiveUsers')}
          </h3>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-600"><X style={{ width: 18, height: 18 }} /></button>
        </div>
        <p className="text-xs text-slate-400 mb-4">
          {data.activeCount} {t('sapUser.activeUsers')} {users.length} {t('sapUser.dialogUsers2')} · {t('sapUser.inactiveUsersExcluded')}
        </p>

        <div className="flex gap-4 items-start">
          {/* Matriks — dibuat sempit agar donut muat di samping */}
          <div className="rounded-xl border border-slate-200 overflow-x-auto flex-1 min-w-0">
            <table className="w-full text-[11px] whitespace-nowrap">
              <thead className="sticky top-0 bg-emerald-50 z-10">
                <tr className="text-emerald-800 border-b border-emerald-100">
                  <th className="text-left px-2 py-1.5 font-bold">Dept</th>
                  {data.lics.map(l => (
                    <th key={l} className="text-right px-2 py-1.5 font-bold max-w-[90px]">
                      <span className="inline-flex items-center gap-1 justify-end">
                        <span className="inline-block w-2 h-2 rounded-full shrink-0" style={{ background: data.colorOf[l] }} />
                        <span className="truncate" title={l}>{l.replace(/^SAP /, '')}</span>
                      </span>
                    </th>
                  ))}
                  <th className="text-right px-2 py-1.5 font-bold">Total</th>
                </tr>
              </thead>
              <tbody>
                {data.companies.map(c => (
                  <React.Fragment key={c.company}>
                    {c.rows.map(r => (
                      <tr key={`${c.company}-${r.module}`} className="border-b border-slate-50">
                        <td className="px-2 py-1 text-slate-700">{c.company}-{r.module}</td>
                        {r.counts.map((n, i) => <td key={i} className="px-2 py-1 text-right text-slate-500">{n || ''}</td>)}
                        <td className="px-2 py-1 text-right text-slate-400">{r.counts.reduce((a, b) => a + b, 0)}</td>
                      </tr>
                    ))}
                    <tr className="border-b border-slate-200 bg-slate-50 font-bold text-slate-700">
                      <td className="px-2 py-1 text-right">{c.company} — Total</td>
                      {c.sub.map((n, i) => <td key={i} className="px-2 py-1 text-right">{n || ''}</td>)}
                      <td className="px-2 py-1 text-right text-emerald-700">{c.total}</td>
                    </tr>
                  </React.Fragment>
                ))}
              </tbody>
              <tfoot>
                <tr className="border-t-2 border-emerald-200 bg-emerald-50 font-bold text-emerald-800">
                  <td className="px-2 py-1.5">{t('sapUser.totalUsage')}</td>
                  {data.grand.map((n, i) => <td key={i} className="px-2 py-1.5 text-right">{n}</td>)}
                  <td className="px-2 py-1.5 text-right">{data.grandTotal}</td>
                </tr>
              </tfoot>
            </table>
          </div>

          {/* Kolom kanan: dua chart ditumpuk — Distribusi per Company di atas, Distribusi License (semua company) di bawah */}
          <div className="shrink-0 flex flex-col gap-4" style={{ width: 260 }}>
            <div className="flex flex-col items-center rounded-xl border border-slate-100 p-3">
              {(() => {
                // Cari index license target berdasarkan deskripsi.
                const isTarget = (l) => { const t = l.toLowerCase(); return t.includes('application professional') || t.includes('application limited'); };
                const tIdx = data.lics.map((l, i) => [l, i]).filter(([l]) => isTarget(l)).map(([, i]) => i);
                // Nilai per company = jumlah user (aktif) yg license-nya termasuk target.
                const compColors = ['#3b82f6', '#f59e0b', '#8b5cf6', '#10b981', '#ef4444', '#14b8a6', '#f43f5e', '#0ea5e9', '#a3a3a3'];
                const slices = data.companies
                  .map((c, i) => ({ label: c.company, value: tIdx.reduce((t, ix) => t + c.sub[ix], 0), color: compColors[i % compColors.length] }))
                  .filter(s => s.value > 0);
                const total = slices.reduce((a, s) => a + s.value, 0);
                return (
                  <>
                    <div className="text-xs font-bold text-slate-600 text-center mb-2">
                      {t('sapUser.distributionPerCompany')}
                      <div className="text-[10px] font-normal text-slate-400">App Professional + App Limited Pro</div>
                    </div>
                    <DonutChart size={150} stroke={30} slices={slices} title={`${total}`} subtitle={t('sapUser.users')} />
                    <div className="mt-3 w-full space-y-1">
                      {slices.map(s => (
                        <div key={s.label} className="flex items-center gap-2 text-xs">
                          <span className="inline-block w-3 h-3 rounded-sm shrink-0" style={{ background: s.color }} />
                          <span className="text-slate-600 flex-1 truncate">{s.label}</span>
                          <span className="font-semibold text-slate-700">{s.value}</span>
                          <span className="text-slate-400" style={{ width: 40, textAlign: 'right' }}>{total ? Math.round(s.value / total * 100) : 0}%</span>
                        </div>
                      ))}
                    </div>
                  </>
                );
              })()}
            </div>

            {/* Distribusi License (semua company) — pindah ke sini, di bawah chart pertama */}
            <div className="flex flex-col items-center rounded-xl border border-slate-100 p-3">
              <div className="text-xs font-bold text-slate-600 text-center mb-2 uppercase tracking-wide">
                {t('sapUser.licenseDistributionAllCompanies')}
              </div>
              <DonutChart size={150} stroke={30}
                slices={data.lics.map((l, i) => ({ label: l, value: data.grand[i], color: data.colorOf[l] }))}
                title={`${data.grandTotal}`} subtitle={t('sapUser.active2')} />
              <div className="mt-3 w-full space-y-1">
                {data.lics.map((l, i) => (
                  <div key={l} className="flex items-center gap-2 text-xs">
                    <span className="inline-block w-3 h-3 rounded-sm shrink-0" style={{ background: data.colorOf[l] }} />
                    <span className="text-slate-600 flex-1 truncate" title={l}>{l}</span>
                    <span className="font-semibold text-slate-700">{data.grand[i]}</span>
                    <span className="text-slate-400" style={{ width: 40, textAlign: 'right' }}>{data.grandTotal ? Math.round(data.grand[i] / data.grandTotal * 100) : 0}%</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function SortTh({ k, label, sortKey, sortDir, onSort }) {
  const active = sortKey === k;
  return (
    <th className="text-left px-3 py-2.5 font-semibold select-none cursor-pointer hover:text-slate-700" onClick={() => onSort(k)}>
      <span className="inline-flex items-center gap-1">
        {label}
        <span className={`text-[10px] ${active ? 'text-blue-500' : 'text-slate-300'}`}>{active ? (sortDir === 'asc' ? '▲' : '▼') : '↕'}</span>
      </span>
    </th>
  );
}

function FilterPill({ active, onClick, label }) {
  return (
    <button onClick={onClick}
      className={`px-2.5 py-1 rounded-full text-xs font-semibold border transition-all
        ${active ? 'bg-blue-600 text-white border-blue-600' : 'bg-white text-slate-500 border-slate-200 hover:border-blue-300'}`}>
      {label}
    </button>
  );
}

function ResetPasswordModal({ username, id, busy, onClose, onSubmit }) {
  const {
    t
  } = useLang();

  const [pw, setPw] = useState('');
  return (
    <div className="fixed inset-0 z-[1000] flex items-center justify-center bg-slate-900/50 p-4">
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-sm p-5">
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-display font-bold text-slate-800 text-sm flex items-center gap-2"><KeyRound className="text-blue-500" style={{ width: 16, height: 16 }} /> {id ? 'Reset Password' : 'Reset Password'}</h3>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-600"><X style={{ width: 18, height: 18 }} /></button>
        </div>
        <p className="text-xs text-slate-500 mb-3">
          {id ? 'User: ' : 'User: '}<span className="font-semibold text-slate-700">{username}</span>.{' '}
          {t('sapUser.newPasswordInitialUserMust')}
        </p>
        <label className="block text-xs font-semibold text-slate-500 mb-1">{t('sapUser.newPassword')}</label>
        <input type="text" value={pw} onChange={(e) => setPw(e.target.value)} autoFocus placeholder={t('sapUser.eGWelcome1')}
          className="w-full px-3 py-2 rounded-xl border border-slate-200 text-sm outline-none focus:border-blue-300 mb-4" />
        <div className="flex justify-end gap-2">
          <button onClick={onClose} className="px-3 py-2 rounded-xl text-sm font-medium border border-slate-200 text-slate-600 hover:bg-slate-50">{t('sapUser.cancel')}</button>
          <button onClick={() => pw && onSubmit(pw)} disabled={!pw || busy} className="px-4 py-2 rounded-xl text-sm font-semibold bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed">{busy ? (t('sapUser.processing')) : 'Reset'}</button>
        </div>
      </div>
    </div>
  );
}
